import { Link, useParams } from "react-router-dom";
import PageHero from "@/components/PageHero";
import { LOCATIONS, getLocationBySlug } from "@/data/locations";
import NotFound from "@/pages/NotFound";

export default function LocationPage() {
  const { slug } = useParams<{ slug: string }>();
  const location = getLocationBySlug(slug);

  if (!location) {
    return <NotFound />;
  }

  const other = LOCATIONS.find((l) => l.slug !== location.slug);

  return (
    <>
      <PageHero
        eyebrow={`${location.shortName}, Connecticut`}
        title={location.name}
        intro={location.description}
        crumbs={[
          { label: "Home", to: "/" },
          { label: "Locations", to: "/#locations" },
          { label: location.shortName },
        ]}
      />

      <section className="bg-cream-50">
        <div className="section grid gap-12 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-7">
            <div className="relative aspect-[16/10] overflow-hidden rounded-[2rem] border border-cream-300/60 bg-sage-100 shadow-soft">
              {/* Placeholder image: bright, welcoming photo of the office reception
                  area with natural light, soft greenery, and a tasteful sign. */}
              <div className="flex h-full w-full items-center justify-center p-10 text-center">
                <p className="font-serif text-sm italic text-sage-600">
                  Photo of the {location.shortName} office
                  <br />
                  <span className="text-xs not-italic text-sage-500">
                    (image coming soon)
                  </span>
                </p>
              </div>
            </div>

            <div className="mt-10 space-y-6 text-sage-700/90">
              <div>
                <h2 className="font-serif text-2xl text-sage-800">
                  Visiting the {location.shortName} office
                </h2>
                <p className="mt-3">{location.neighborhood}</p>
              </div>
              <div>
                <h3 className="font-serif text-lg text-sage-800">Parking & access</h3>
                <p className="mt-2 text-sm leading-relaxed">{location.parking}</p>
              </div>
              <div>
                <h3 className="font-serif text-lg text-sage-800">New here?</h3>
                <p className="mt-2 text-sm leading-relaxed">
                  Plan to arrive about 10 minutes early so we have time to get
                  you settled. We'll walk you through what to expect, answer any
                  questions, and make sure you leave feeling confident about
                  next steps.
                </p>
              </div>
            </div>
          </div>

          <aside className="lg:col-span-5">
            <div className="sticky top-28 space-y-6">
              <div className="rounded-2xl border border-sage-100 bg-cream-100/60 p-6">
                <h3 className="font-serif text-lg text-sage-800">
                  {location.name}
                </h3>
                <div className="mt-5 space-y-5 text-sm">
                  <Row label="Address">
                    {location.address.map((line) => (
                      <p key={line} className="text-sage-700/90">
                        {line}
                      </p>
                    ))}
                    <a
                      href={location.directionsHref}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-2 inline-flex items-center gap-1 text-blush-500 hover:text-blush-600"
                    >
                      Get directions <span aria-hidden="true">→</span>
                    </a>
                  </Row>

                  <Row label="Phone">
                    <a
                      href={location.phoneHref}
                      className="font-serif text-lg text-sage-800 hover:text-blush-500"
                    >
                      {location.phone}
                    </a>
                    <p className="mt-1 text-sage-700/70">Fax: {location.fax}</p>
                  </Row>

                  <Row label="Hours">
                    <ul className="space-y-1.5">
                      {location.hours.map((h) => (
                        <li
                          key={h.label}
                          className="flex justify-between gap-6 text-sage-700/90"
                        >
                          <span>{h.label}</span>
                          <span className="text-sage-800">{h.value}</span>
                        </li>
                      ))}
                    </ul>
                  </Row>
                </div>

                <Link
                  to="/#contact"
                  className="btn-primary mt-6 w-full text-xs"
                >
                  Request an appointment
                </Link>
              </div>

              {other && (
                <div className="rounded-2xl border border-blush-100 bg-blush-50/60 p-6">
                  <p className="text-xs font-medium uppercase tracking-[0.18em] text-blush-500">
                    Closer to {other.shortName}?
                  </p>
                  <p className="mt-2 text-sm leading-relaxed text-sage-700/90">
                    Same warm welcome, same standard of care — just down the
                    road.
                  </p>
                  <Link
                    to={`/locations/${other.slug}`}
                    className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-blush-500 hover:gap-2"
                  >
                    Visit our {other.shortName} office <span aria-hidden="true">→</span>
                  </Link>
                </div>
              )}
            </div>
          </aside>
        </div>
      </section>

      <section className="bg-sage-50/60">
        <div className="section">
          <p className="eyebrow">Find us</p>
          <h2 className="mt-3 font-serif text-2xl text-sage-800 sm:text-3xl">
            {location.fullAddress}
          </h2>
          <div className="mt-8 overflow-hidden rounded-[2rem] border border-sage-100 bg-cream-50 shadow-soft">
            <iframe
              title={`Map of ${location.name}`}
              src={location.mapEmbedSrc}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="h-[420px] w-full border-0"
              allowFullScreen
            />
          </div>
          <div className="mt-6">
            <a
              href={location.directionsHref}
              target="_blank"
              rel="noreferrer"
              className="btn-secondary"
            >
              Open in Google Maps
            </a>
          </div>
        </div>
      </section>
    </>
  );
}

function Row({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="grid gap-2 sm:grid-cols-[5.5rem,1fr] sm:items-start">
      <p className="text-xs font-medium uppercase tracking-[0.18em] text-blush-500">
        {label}
      </p>
      <div>{children}</div>
    </div>
  );
}
